import type { Socket } from 'socket.io-client'
import { IS_ELECTRON } from './config'

type PresenceSocket = Socket & { __presenceConnectHooked?: boolean }
type PresenceWindow = Window & { __socket?: Socket; __presenceListenersInstalled?: boolean }

// Reports foreground/background to the server ('presence:app-state' → Redis
// active set) so push suppression knows whether ANY device is actively
// viewing the app.
//  - Browser tabs + Capacitor WebViews: visibility (the WebView flips to
//    'hidden' when the app backgrounds).
//  - Electron: the shell sets backgroundThrottling:false, which pins
//    visibilityState to 'visible' even minimized/in tray — use focus instead.
// Emits are volatile (dropped when disconnected): presence is latest-wins,
// and each socket's 'connect' listener re-reports authoritatively. If the
// WebView is suspended before a final report flushes, the server's
// disconnect cleanup + 12h TTL bound the staleness.

function computeActive(): boolean {
  return IS_ELECTRON ? document.hasFocus() : document.visibilityState === 'visible'
}

function report(): void {
  const win = window as PresenceWindow
  const socket = win.__socket
  if (!socket) return
  socket.volatile.emit('presence:app-state', { active: computeActive() })
}

export function installAppPresence(socket: Socket): void {
  const presenceSocket = socket as PresenceSocket
  const win = window as PresenceWindow

  // ONE-TIME: install document/window listeners on the window object itself
  // (not the socket) so they survive HMR and socket recreation. `report()`
  // resolves the live socket at event time via window.__socket, so these
  // listeners never close over a stale (dead) socket instance.
  if (!win.__presenceListenersInstalled) {
    win.__presenceListenersInstalled = true

    document.addEventListener('visibilitychange', report)

    window.addEventListener('pagehide', () => {
      // Best-effort send-before-unload on the live socket.
      const s = (window as PresenceWindow).__socket
      if (s) {
        try { s.volatile.emit('presence:app-state', { active: false }) } catch { /* defensive against torn-down socket during page teardown */ }
      }
    })

    // Electron: visibilityState is pinned 'visible' due to backgroundThrottling:false,
    // so derive active from window focus instead.
    if (IS_ELECTRON) {
      window.addEventListener('focus', report)
      window.addEventListener('blur', report)
    }
  }

  // PER-SOCKET: install a connect listener on each new socket instance so
  // that every reconnect re-reports the current state authoritatively.
  if (!presenceSocket.__presenceConnectHooked) {
    presenceSocket.__presenceConnectHooked = true
    presenceSocket.on('connect', report)
  }

  if (socket.connected) report()
}
